import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** A presentation enhancement; validated Liquid facts and Shopify own the purchase. */
class AtelierPurchase extends Component {
  #listeners;
  #observer;
  #root;
  #data;
  #busy = false;
  #uncertain = false;
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.#root = this.closest('fivepoint-pdp');
    if (!this.#root) return;
    try { this.#data = JSON.parse(this.#root.querySelector('[data-fp-data]').textContent); }
    catch { this.#status(this.dataset.invalid); }
    const options = { signal: this.#listeners.signal };
    this.#root.addEventListener('submit', this.#submit, { ...options, capture: true });
    this.#root.addEventListener('atelier:selection', this.#sync, options);
    this.querySelector('[data-atelier-mobile-add]')?.addEventListener('click', () => {
      const button = this.#root.querySelector('[data-fp-add] button');
      if (button?.disabled) { this.#root.openSheet(); return; }
      button?.form?.requestSubmit(button);
    }, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      event.promise?.then(({ detail }) => {
        if (!detail?.didError && Array.isArray(detail?.items)) this.#inBag(detail.items);
      }).catch(() => { /* The cart's alert owns update failures. Never show a new success. */ });
    }, options);
    const button = this.#root.querySelector('[data-fp-add] button');
    if (button && typeof IntersectionObserver !== 'undefined') {
      this.#observer = new IntersectionObserver(([entry]) => {
        const bar = this.querySelector('[data-atelier-mobile]');
        if (bar) bar.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
      });
      this.#observer.observe(button);
    }
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort(); this.#observer?.disconnect(); clearTimeout(this.#timer);
    // Do not cancel a cart write: an aborted write still may have committed.
  }

  #status(text) { this.querySelector('[data-atelier-status]').textContent = text; }

  #sync = () => {
    const main = this.#root.querySelector('[data-fp-add] button');
    if (this.#busy || this.#uncertain) this.#root.querySelectorAll('form button[type="submit"]').forEach(button => { button.disabled = true; });
    const label = this.querySelector('[data-atelier-mobile-label]');
    if (label) label.textContent = this.#root.querySelector('[data-fp-colour-head]')?.textContent || main?.textContent || '';
    const mobile = this.querySelector('[data-atelier-mobile-add]');
    if (mobile) {
      mobile.textContent = main?.textContent || this.dataset.invalid;
      // Incomplete selection scrolls to the chooser; busy/unknown writes cannot resubmit.
      mobile.disabled = this.#busy || this.#uncertain;
    }
  };

  #selection(form) {
    if (!this.#data || !Array.isArray(this.#data.colours)) return null;
    const values = new FormData(form);
    const id = String(values.get('id') || ''), quantity = Number(values.get('quantity'));
    const variant = [this.#data.swatch, ...this.#data.colours].find(v => v && String(v.id) === id);
    const card = variant === this.#data.swatch;
    const max = card ? 1 : Math.min(variant?.max ?? this.#data.maxQty, this.#data.maxQty);
    if (this.#data.pricingState !== 'approved' || !variant?.available || variant.other || !/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(quantity) || !Number.isInteger(variant.min) || !Number.isInteger(variant.step) || variant.step < 1 || quantity < variant.min || quantity > max || (quantity - variant.min) % variant.step !== 0 || (card && quantity !== 1)) return null;
    return { id, quantity };
  }

  #submit = async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (this.#busy || this.#uncertain) return;
    const choice = this.#selection(form);
    if (!choice) { this.#status(this.dataset.invalid); return; }
    if (!form.reportValidity()) return;
    // A merchant-selected cart page uses the platform's normal form submission.
    if (this.dataset.cartType !== 'drawer') { HTMLFormElement.prototype.submit.call(form); return; }
    const bag = document.getElementById('cart-drawer');
    if (!bag || typeof bag.addProduct !== 'function') { this.#status(this.dataset.failed); return; }
    clearTimeout(this.#timer);
    this.#busy = true; this.#root.setAttribute('aria-busy', 'true');
    const buttons = [...this.#root.querySelectorAll('form button[type="submit"]')].map(button => [button, button.disabled]);
    this.#status(this.dataset.adding); this.#sync();
    try {
      const cart = await bag.addProduct(choice.id, choice.quantity, { page_type: 'product', source_surface: 'storefront', component_id: 'atelier-a-purchase' });
      if (!this.isConnected) return;
      this.#inBag(cart.items); this.#status(this.dataset.added);
      this.#timer = setTimeout(() => { if (this.isConnected) this.#status(''); }, 1500);
    } catch (error) {
      this.#uncertain = error.cartResult !== 'rejected';
      if (!this.isConnected) return;
      this.#status(this.#uncertain ? this.dataset.uncertain : error.message || this.dataset.failed);
      this.querySelector('[data-atelier-review]').hidden = !this.#uncertain;
    } finally {
      this.#busy = false;
      if (this.isConnected) {
        for (const [button, disabled] of buttons) button.disabled = disabled;
        this.#root.removeAttribute('aria-busy'); this.#root.render(); this.#sync();
      }
    }
  };

  #inBag(items) {
    const own = items.filter(item => String(item.product_id) === this.#root.dataset.productId);
    const node = this.querySelector('[data-atelier-in-bag]');
    node.hidden = own.length === 0;
    node.textContent = own.length ? `${this.dataset.inBag} ${own.map(item => `${item.variant_title} × ${item.quantity}`).join(' · ')}` : '';
  }
}

if (!customElements.get('fivepoint-atelier-purchase')) customElements.define('fivepoint-atelier-purchase', AtelierPurchase);
